import React, { FC, useEffect, useRef, useState } from 'react';
import MuiContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box } from '@mui/material';
import Image from 'next/image';
import useTranslation from 'next-translate/useTranslation';

import {
  OnboardingModalActions,
  useOnboardingModalActionsCompact,
} from '@/components/MainModal/OnboardingModalActions';
import { INVITE_LINK_SNACKBAR_AUTO_HIDE_MS } from '@/components/Steps/InviteTeamStep/inviteStepOptimization.util';
import {
  useAppDispatch,
  useAppSelector,
  useClipboard,
  useDownloadApps,
  useOnboardingTask,
  useTracking,
} from '@/hooks';
import { selectCompanyId } from '@/redux/features/authSlice';
import { setDidCopyMagicLink } from '@/redux/features/onboardingSlice';
import CompanyRequests from '@/requests/company.requests';
import { Button } from '@/shared/Button';
import { Snackbar } from '@/shared/Snackbar';
import stepStyles from '@/shared/Step/Step.module.scss';
import StepHeader from '@/shared/StepHeader/StepHeader';
import stepHeaderStyles from '@/shared/StepHeader/StepHeader.module.scss';
import { StepCommonProps } from '@/types';

export const InviteLinkShareStep: FC<StepCommonProps> = ({ flow }) => {
  const { copyToClipboard } = useClipboard();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { goToNextStep, goToPreviousStep } = useOnboardingTask(flow);
  const { trackOnboardingStep } = useTracking(flow);
  const { executeBrowserPromoTracking } = useDownloadApps(flow);
  const compactActions = useOnboardingModalActionsCompact();
  const { company } = useAppSelector(state => state.auth);
  const companyId = useAppSelector(selectCompanyId);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [showCopiedButtonState, setShowCopiedButtonState] = useState(false);
  const hasTracked = useRef(false);
  const revertTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (hasTracked.current || !companyId) {
      return;
    }

    CompanyRequests.putCompanySettings(companyId, {
      custom: {
        ...company?.companySettings?.custom,
        hasInviteLink: true,
      },
    }).catch(() => undefined);

    hasTracked.current = true;
  }, [company, companyId]);

  useEffect(() => () => {
    if (revertTimerRef.current) clearTimeout(revertTimerRef.current);
  }, []);

  const handleCopy = async () => {
    const { success } = await copyToClipboard(company?.inviteLink || '');
    if (success) {
      dispatch(setDidCopyMagicLink(true));
      setSnackbarMessage(t('onboarding:invite_members_step.optimization_b.toast_link_copied'));
      setSnackbarOpen(true);
      setShowCopiedButtonState(true);
      if (revertTimerRef.current) clearTimeout(revertTimerRef.current);
      revertTimerRef.current = setTimeout(() => {
        setShowCopiedButtonState(false);
        revertTimerRef.current = null;
      }, INVITE_LINK_SNACKBAR_AUTO_HIDE_MS);
    } else {
      setSnackbarMessage(t('onboarding:invite_members_step.magic_link.link_not_copied'));
      setSnackbarOpen(true);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
    setShowCopiedButtonState(false);
    if (revertTimerRef.current) {
      clearTimeout(revertTimerRef.current);
      revertTimerRef.current = null;
    }
  };

  const handleGoToDashboard = async () => {
    await executeBrowserPromoTracking();
    await trackOnboardingStep('Skipped Invite User during Onboarding');
    goToNextStep();
  };

  return (
    <Box
      className="d-flex-col"
      data-invite-link-share-step
      sx={{
        height: '100%',
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          flex: '1 1 auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mx: 'auto',
            mb: { zero: 1.5, sm: 2 },
          }}
        >
          <Image
            src="/assets/onboarding/add_employee.svg"
            alt=""
            width={ 104 }
            height={ 104 }
            style={{ display: 'block' }}
          />
        </Box>

        <Box
          sx={{
            width: '100%',
            [`& .${ stepHeaderStyles.StepHeader__Desc }`]: {
              color: 'var(--color-secondary-charcoal)',
            },
          }}
        >
          <StepHeader
            title="onboarding:invite_members_step.optimization_c.link_step.title"
            desc="onboarding:invite_members_step.optimization_c.link_step.desc"
            className={ stepStyles.Step__TextWrap }
          />
        </Box>

        <Button
          variant="contained"
          color="primary"
          size="medium"
          uppercase
          disabled={ showCopiedButtonState }
          onClick={ handleCopy }
          trackEvent="Copied url on Invite Screen"
          trackProps={{ invite_url: company?.inviteLink }}
          startIcon={ <MuiContentCopyIcon sx={{ fontSize: 18 }} /> }
          sx={{
            minWidth: 192,
            width: 'max-content',
            maxWidth: '100%',
            height: 36,
            minHeight: 36,
            boxSizing: 'border-box',
            borderRadius: '4px',
            opacity: 1,
            padding: '8px 16px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            flexShrink: 0,
            whiteSpace: 'nowrap',
            mt: { zero: 1, sm: 2 },
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.75px',
            fontWeight: 500,
            '& .MuiButton-startIcon': {
              marginRight: 0,
              marginLeft: 0,
            },
            '& .MuiButton-label': {
              whiteSpace: 'nowrap',
            },
          }}
        >
          { showCopiedButtonState
            ? t('onboarding:invite_members_step.optimization_c.link_step.btn_copied')
            : t('onboarding:invite_members_step.optimization_b.copy_invite_link') }
        </Button>
      </Box>

      <OnboardingModalActions>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
          flexDirection="row"
          flexWrap="wrap"
          gap={ 2 }
          mt={ compactActions ? 0 : 'auto' }
          sx={ compactActions ? { pt: 0 } : { pt: 3 } }
        >
          <Button
            variant="outlined"
            size="medium"
            uppercase
            onClick={ () => goToPreviousStep() }
            sx={{ fontSize: '14px !important', height: '36px' }}
          >
            { t('common:back') }
          </Button>
          <Button
            variant="outlined"
            size="medium"
            uppercase
            onClick={ handleGoToDashboard }
            sx={{ fontSize: '14px !important', height: '36px' }}
          >
            { t('onboarding:invite_members_step.optimization_b.go_to_dashboard_btn') }
          </Button>
        </Box>
      </OnboardingModalActions>

      <Snackbar
        open={ snackbarOpen }
        onClose={ handleCloseSnackbar }
        message={ snackbarMessage }
        autoHideDuration={ INVITE_LINK_SNACKBAR_AUTO_HIDE_MS }
      />
    </Box>
  );
};
